import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import config from 'common/config';
import callApi from 'helpers/apiCaller';
import { getAuthHeader } from 'helpers/authHelper';

const PRESIGNED_FACE_ID = '/gd/v3/student_exams/cmc/presigned_upload_url';

/**
 * Lấy presigned S3 URL để upload ảnh khuôn mặt.
 */
async function getFacePresignedUrl(filePath) {
  const header = getAuthHeader();
  const body = { type: 'face_id', file_paths: [filePath] };
  const res = await axios.post(config.getRootURL() + PRESIGNED_FACE_ID, body, {
    headers: header
  });
  return res.data?.data?.presigned_urls || null;
}

/**
 * PUT file lên S3 qua presigned URL.
 */
async function putFileToS3(file, presignedUrls) {
  const key = Object.keys(presignedUrls)[0];
  const res = await axios.put(presignedUrls[key], file, {
    headers: { 'Content-Type': file.type }
  });
  return res.status === 200;
}

/**
 * Đăng ký khuôn mặt:
 *   1. Upload ảnh lên S3 qua /student_exams/cmc/presigned_upload_url (type: face_id)
 *   2. POST s3_path lên /gd/v3/user/face_login_thi
 *
 * @param {File|Blob} file
 * @param {Function} onError
 * @returns {Promise<{status: number, data: object}|null>}
 */
export async function faceRegister(file, onError) {
  const header = getAuthHeader();

  try {
    const imageFile =
      file instanceof File
        ? file
        : new File([file], 'face.jpg', { type: file.type || 'image/jpeg' });

    // 1. Tạo đường dẫn S3
    const ext      = imageFile.name.split('.').pop() || 'jpg';
    const s3Path   = `face_id/${uuidv4()}.${ext}`;

    // 2. Lấy presigned URL
    const presignedUrls = await getFacePresignedUrl(s3Path);
    if (!presignedUrls) {
      const err = { message: 'Không lấy được presigned URL để upload ảnh.' };
      if (onError) onError(err);
      return null;
    }

    // 3. Upload file lên S3
    const uploaded = await putFileToS3(imageFile, presignedUrls);
    if (!uploaded) {
      const err = { message: 'Upload ảnh lên S3 thất bại.' };
      if (onError) onError(err);
      return null;
    }

    // 4. Gửi đường dẫn S3 lên API đăng ký
    const url      = config.getRootURL() + '/gd/v3/user/face_login_thi';
    const formData = new FormData();
    formData.append('face_login', s3Path);

    const res = await fetch(url, {
      method: 'POST',
      body: formData,
      headers: header
    });

    const text = await res.text();
    const json = text ? JSON.parse(text) : {};

    if (!res.ok) {
      if (onError) onError({ response: { data: json, status: res.status } });
      return null;
    }

    return { status: res.status, data: json };
  } catch (err) {
    if (onError) onError(err);
    return null;
  }
}

/**
 * Đăng nhập bằng khuôn mặt — POST FormData { user_id, face_image: File }.
 * @param {{ user_id: string, face_image: File }} payload
 * @returns {Promise<{data: object|null, error: object|null, status: number|null}>}
 */
export async function faceLogin(payload) {
  const url      = config.getRootURL() + '/gd/v3/face_login_thi';
  const formData = new FormData();
  if (payload) {
    Object.keys(payload).forEach((key) => formData.append(key, payload[key]));
  }

  try {
    const res  = await fetch(url, { method: 'POST', body: formData });
    const text = await res.text();
    const json = text ? JSON.parse(text) : {};

    if (!res.ok) {
      return { data: null, error: json, status: res.status };
    }

    return { data: json, error: null, status: res.status };
  } catch (err) {
    return { data: null, error: { message: err?.message }, status: null };
  }
}

/**
 * Lấy lịch sử đăng nhập.
 */
export function fetchLoginHistory({ perPage = 10, pageId = 1 } = {}) {
  const header = getAuthHeader();
  const url    = `${config.getRootURL()}/gd/v3/login_histories?per_page=${perPage}&page_id=${pageId}`;
  return callApi(url, 'GET', {}, header);
}
